import { ISOString } from '@jumpstart/core';
import { useAutocomplete } from '@mui/lab';
import Badge from '@mui/material/Badge';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Timestamp } from 'firebase/firestore';
import moment from 'moment';
import React, { Fragment, useState } from 'react';
import { ISubscriber } from '../schema';


interface ISubscriberEnhanced extends ISubscriber {
  notifications: number;
  lastChecked?: ISOString;
}


// If last check exists today, then good, if not then not checked
const todayStart = moment().startOf('day');

function CustomerMenuItem(props: { subscriber: ISubscriberEnhanced; onSelect: (subscriber: ISubscriberEnhanced) => void; selected: ISubscriber | null }) {
  const { subscriber, onSelect, selected } = props;

  const labelId = `checkbox-list-secondary-label-${subscriber.uid}`;

  // @TODO enable if we want to track task management
  // const handleCheck = (check: boolean) => {
  //   console.log('Handle Check...');
  //   updateDoc(
  //     doc(db, 'scout9-sms-subscribers', subscriber.uid),
  //     { lastChecked: check ? new Date().toISOString() : null }
  //   )
  //     .catch((err: Error) => {
  //       console.error('Failed', err);
  //     });
  // };

  return (
    <ListItem
      key={subscriber.phone}
      selected={!!selected ? subscriber.phone === selected.phone : false}
    >
      <Tooltip title={subscriber.uid} enterDelay={3000} leaveDelay={0} enterNextDelay={1000}>
        <ListItemButton
          onClick={() => {
            onSelect(subscriber);
          }}
        >
          <Badge badgeContent={subscriber.notifications} color="primary" invisible={subscriber.notifications < 1}>
            <ListItemText
              id={labelId}
              primary={subscriber.phone}
              primaryTypographyProps={{ variant: 'body2' }}
              secondary={`${subscriber.email ? subscriber.email : ''} ${subscriber.testUser ? '(T)' : ''}`}
              secondaryTypographyProps={{ variant: 'caption' }}
              style={{ textDecoration: subscriber.end ? ' line-through' : 'unset' }}
            />
          </Badge>
          {/*<ListItemSecondaryAction>*/}
          {/*  <Checkbox*/}
          {/*    edge="end"*/}
          {/*    onChange={(event) => handleCheck(event.target.checked)}*/}
          {/*    checked={subscriber.lastChecked ? moment(subscriber.lastChecked)*/}
          {/*      .isAfter(todayStart) && subscriber.notifications === 0 : false}*/}
          {/*    inputProps={{ 'aria-labelledby': labelId }}*/}
          {/*  />*/}
          {/*</ListItemSecondaryAction>*/}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

}

export interface ICustomersListView {
  subscribers: ISubscriber[];
  loading: boolean;
  selected: ISubscriber | null;

  onSelect(subscriber: ISubscriber | null): void;
}


export default function CustomersListView(props: ICustomersListView) {
  const { onSelect, selected, subscribers, loading } = props;
  const [error, setError] = useState<Error | null>(null);
  const sortSubscribers = (a: ISubscriberEnhanced, b: ISubscriberEnhanced) => b.notifications - a.notifications;

  const enhance = (s: ISubscriber): ISubscriberEnhanced => {
    let notifications = 0;
    let minsLastSent = null;
    let minsLastReceive = null;
    if (s.lastSent) {
      minsLastSent = moment()
        .diff(moment(new Timestamp(s.lastSent.seconds, s.lastSent.nanoseconds).toDate()), 'minutes');
    }
    if (s.lastReceive) {
      minsLastReceive = moment()
        .diff(
          moment(new Timestamp(s.lastReceive.seconds, s.lastReceive.nanoseconds).toDate()),
          'minutes'
        );
    }

    // Message waiting for a reply
    // @TODO fix me
    if (s.phone === '+14254460552' || s.email === 'opiepat@gmail.com') {
      console.log(`PATRICK "${s.phone}" "${s.email}"`);
      console.log('Last time I sent ya', minsLastSent);
      console.log('Last time I heard from ya', minsLastReceive);
    }
    if (minsLastReceive) {
      if (typeof minsLastSent !== 'number') {
        console.log(s.phone, 'no last sent...');
        notifications++;
      } else if (minsLastReceive < minsLastSent) {
        console.log(s.phone, 'User waiting for reply...', minsLastReceive, minsLastSent);
        notifications++;
      }
    }
    if (s.end) {
      notifications -= 100000;
    }
    return {
      ...s,
      notifications: notifications
    };
  };


  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    inputValue,
    value
  } = useAutocomplete({
    id: 'customer-search-bar',
    options: subscribers,
    clearOnEscape: true,
    getOptionLabel: (option) => `${option.email ? option.email : ''} ${option.phone}`
  });

  if (!subscribers) {
    return null;
  }

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      sx={{
        height: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
        width: '100%',
        padding: '16px 0',
        // maxWidth: 360,
        backgroundColor: (theme) => theme.palette.background.paper
      }}
      {...getRootProps()}
    >
      <TextField
        placeholder={'Search...'}
        sx={{
          width: 350,
          borderRadius: '10px',
          border: 'unset',
          cursor: 'pointer'
        }}
        variant="outlined"
        InputProps={{
          notched: false,
          sx: {
            backgroundColor: '#F8F8F9',
            borderRadius: '10px',
            color: '#B5B7C4',
            border: 'unset',
            fontWeight: 400,
            marginBottom: 4,

            // @TODO This is a hack
            // minWidth: 'unset',
            // maxWidth: 320,


            userSelect: 'none',
            '& ::placeholder': {
              opacity: 1,
              fontWeight: 400
            },
            '.MuiOutlinedInput-notchedOutline': {
              opacity: 0
            }
          },
          ...(getInputProps() as any)
        }}
      />
      <List
        dense
        {...getListboxProps()}
      >
        <Fragment>
          <ListItem>
            <ListItemButton
              onClick={() => {
                setError(new Error('This functionality is not implemented yet'));
              }}
            >
              <ListItemText primary="Add Customer"/>
            </ListItemButton>
          </ListItem>

          {/*{(groupedOptions.length === 0 || !inputValue ? options.current : groupedOptions)*/}
          {(groupedOptions.length > 0 && !!inputValue ? (groupedOptions as any) : !inputValue ? subscribers : [])
            .map(enhance)
            .sort(sortSubscribers)
            .map((subscriber: ISubscriberEnhanced) => <CustomerMenuItem
              key={subscriber.uid}
              subscriber={subscriber}
              selected={selected}
              onSelect={onSelect}
            />)
          }
          {groupedOptions.length === 0 && !!inputValue &&
          <ListItem>
            <ListItemButton>
              <ListItemText primary="No customers found"/>
            </ListItemButton>
          </ListItem>
          }
        </Fragment>
      </List>
      {loading ? <CircularProgress size={16}/> :
        <Typography variant="caption" style={{ textAlign: 'center' }}/>}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error ? error.message : ''}
      />
    </Grid>
  );
}
